import { type ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChurchIcon from '@mui/icons-material/Church';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '@/hooks/useAuth';

const DRAWER_WIDTH = 240;

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const navItems = [
  { label: 'Overview', icon: DashboardIcon, color: '#1976d2', path: '/platform', exact: true },
  { label: 'Churches', icon: ChurchIcon, color: '#2e7d32', path: '/platform/churches', exact: false },
  { label: 'Users', icon: PeopleIcon, color: '#9c27b0', path: '/platform/users', exact: false },
  { label: 'Analytics', icon: BarChartIcon, color: '#ed6c02', path: '/platform/analytics', exact: false },
  { label: 'Audit Logs', icon: HistoryIcon, color: '#757575', path: '/platform/audit-logs', exact: false },
];

export default function PlatformLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: '#1a1a2e',
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Flock Care
            <Chip
              label="Platform"
              size="small"
              sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
            />
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.displayName || user?.email}
          </Typography>
          <IconButton color="inherit" onClick={logout} aria-label="logout">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <List sx={{ px: 1 }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const selected = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <ListItemButton
                key={item.path}
                selected={selected}
                onClick={() => navigate({ to: item.path })}
                sx={{
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: hexToRgba(item.color, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: '10px',
                      backgroundColor: hexToRgba(item.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: item.color }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: selected ? 600 : 400, fontSize: 14 }}
                />
              </ListItemButton>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            onClick={() => navigate({ to: '/dashboard' })}
            sx={{ mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(117, 117, 117, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20, color: '#757575' }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Back to App"
              primaryTypographyProps={{ fontWeight: 400, fontSize: 14 }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
